import { useContext, useEffect, useRef, useState } from 'react';
import client from 'braintree-web/client';
import type { ApplePay, ApplePaySession} from 'braintree-web';
import { applePay, dataCollector } from 'braintree-web';
import { Skeleton } from '@magpie/ui-core';
import styles from './ApplePayExpressButton.module.scss';
import { WidgetContext } from '../../context/WidgetContextProvider/WidgetContextProvider';
import { useApi, useClientSettings, useExpressWidgetError, useWidgetSettings } from '../../hooks';
import type { GetShippingMethodsQueryParams } from '../../typings/api';
import type { ActiveBasketSummaryData} from '../../typings/basket.types';
import { PaymentMethodType } from '../../typings/basket.types';
import type { ApplePayPayment, ApplePayPaymentContact, ApplePayShippingMethod } from '../../typings/apple.types';
import {
    calculateTotalPrice,
    getLineItems,
    getSelectedShippingMethod,
    retrieveApplePayDeliveryDetails,
} from '../../utils/applePay';
import { getForterToken, getErrorMessageDetails, handleComponentError } from '../../utils';
import { getApplePaySession } from '../../utils/windowGlobals';
import { PAYMENT_TYPES } from '../../typings/payment.types';
import CheckoutRedirect from '../../utils/checkout-redirect';
import { AuthorizationError, ShippingAddressUpdateError, StockAllocationError } from '../../errors';
import { pushPaymentProviderSelectionEvent } from '../../utils/payment-provider-selection-event';
import { HandleGetActiveBasketSummary } from '../../utils/handleGetActiveBasketSummary';
import validateAddress from '../../utils/validateAddress';
import InvalidAddressDetailsError from '../../errors/InvalidAddressDetailsError';
import { useOutOfStockRedirectionTimer } from '../../hooks/useOutOfStockRedirectionTimer';
import { shouldEnforceAllocationExpiry } from '../../utils/shouldEnforceAllocationExpiry';

const APPLE_PAY_VERSION = 3;

// https://developer.apple.com/documentation/apple_pay_on_the_web/applepayvalidatemerchantevent
interface ApplePayValidateMerchantEvent {
    validationURL: string;
}

interface ApplePayShippingMethodSelectedEvent {
    shippingMethod: ApplePayShippingMethod;
}

interface ApplePayExpressButtonProps {
    applePayInstance: ApplePay;
    basket: ActiveBasketSummaryData;
    paymentMethodId: number;
    deviceData: string;
}

interface ApplePayShippingContactSelectedEvent {
    shippingContact: ApplePayPaymentContact;
}

interface ApplePayPaymentAuthorizedEvent {
    payment: ApplePayPayment;
}

const STATUS_SUCCESS = 0;
const STATUS_FAILURE = 1;

export const ApplePayExpressButton = ({
    applePayInstance,
    basket,
    paymentMethodId,
    deviceData,
}: ApplePayExpressButtonProps) => {
    const [, dispatch] = useContext(WidgetContext);
    const { setExpressWidgetError } = useExpressWidgetError();
    const redirectOnOutOfStockTimer = useOutOfStockRedirectionTimer();
    const { currency, registeredCompanyName, expressCheckoutOptions, useMmSessionId } = useClientSettings();

    const API = useApi();
    const { apiBasePath, authToken } = useWidgetSettings();
    const [shippingMethods, setShippingMethods] = useState<ApplePayShippingMethod[]>([]);
    const [isApplePayFlowInProgress, setIsApplePayFlowInProgress] = useState(false);

    const shippingMethodsRef = useRef<ApplePayShippingMethod[]>([]);

    useEffect(() => {
        shippingMethodsRef.current = shippingMethods;
    }, [shippingMethods]);

    const getNextShippingMethods = async (
        basketId: number,
        query?: GetShippingMethodsQueryParams,
        preselectedMethodId?: string,
    ): Promise<ApplePayShippingMethod[]> => {
        const nextShippingMethods = await API.getShippingMethods(basketId, query);

        return nextShippingMethods.map((method, index) => ({
            label: method.name ?? '',
            detail: '',
            amount: method.cost.toString(),
            tax: method.taxCost.toString(),
            identifier: method.id.toString(),
            shipmentId: method.shipmentId,
            selected: preselectedMethodId ? preselectedMethodId === method.id.toString() : index === 0,
        }));
    };

    const onValidateMerchant = async (event: ApplePayValidateMerchantEvent, session: ApplePaySession) => {
        try {
            // Braintree types performValidation as Promise<any>; merchant session is an opaque Apple Pay object.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- opaque Braintree/Apple Pay merchant session
            const merchantSession = await applePayInstance.performValidation({
                validationURL: event.validationURL,
                displayName: registeredCompanyName,
            });

            session.completeMerchantValidation(merchantSession);
        } catch (error) {
            redirectOnOutOfStockTimer.disable();
            handleComponentError(error, setExpressWidgetError);
            setIsApplePayFlowInProgress(false);
            session.abort();
        }
    };

    const onShippingMethodSelected = (event: ApplePayShippingMethodSelectedEvent, session: ApplePaySession) => {
        const currentShippingMethods = shippingMethodsRef.current;

        const nextSelectedShippingMethod = currentShippingMethods.find(
            (method) => method.identifier === event.shippingMethod.identifier,
        );

        try {
            if (nextSelectedShippingMethod) {
                const nextTotalPrice = calculateTotalPrice(basket, nextSelectedShippingMethod);
                session.completeShippingMethodSelection(
                    STATUS_SUCCESS,
                    {
                        label: registeredCompanyName,
                        amount: nextTotalPrice,
                    },
                    getLineItems(basket, nextSelectedShippingMethod),
                );

                setShippingMethods(
                    currentShippingMethods.map((method) => ({
                        ...method,
                        selected: method.identifier === nextSelectedShippingMethod.identifier,
                    })),
                );
            } else {
                throw new Error('No shipping method found!');
            }
        } catch (error) {
            handleComponentError(error, setExpressWidgetError);

            const fallbackShippingMethod = getSelectedShippingMethod(currentShippingMethods);
            session.completeShippingMethodSelection(
                STATUS_FAILURE,
                {
                    label: registeredCompanyName,
                    amount: basket.totalPrice.toString(),
                },
                getLineItems(basket, fallbackShippingMethod),
            );
        }
    };

    const onShippingContactSelected = async (event: ApplePayShippingContactSelectedEvent, session: ApplePaySession) => {
        const { administrativeArea, locality, postalCode } = event.shippingContact;
        const query = { state: administrativeArea, city: locality, zipCode: postalCode };
        const currentShippingMethods = shippingMethodsRef.current;

        try {
            const { id } = basket;
            const currentShippingMethod = getSelectedShippingMethod(currentShippingMethods);

            const nextShippingMethods = await getNextShippingMethods(id, query, currentShippingMethod?.identifier);
            const nextSelectedShippingMethod = getSelectedShippingMethod(nextShippingMethods);
            const newTotalPrice = calculateTotalPrice(basket, nextSelectedShippingMethod);
            const newLineItems = getLineItems(basket, nextSelectedShippingMethod);

            session.completeShippingContactSelection(
                STATUS_SUCCESS,
                nextShippingMethods,
                {
                    label: registeredCompanyName,
                    amount: newTotalPrice,
                },
                newLineItems,
            );

            setShippingMethods(nextShippingMethods);
        } catch (error) {
            if (expressCheckoutOptions?.applePaySettings?.abortOnShippingContactSelectedError) {
                const message = getErrorMessageDetails(error);
                handleComponentError(new ShippingAddressUpdateError(message), setExpressWidgetError);
                redirectOnOutOfStockTimer.disable();
                setIsApplePayFlowInProgress(false);
                session.abort();
            } else {
                handleComponentError(error, setExpressWidgetError);

                session.completeShippingContactSelection(
                    STATUS_FAILURE,
                    currentShippingMethods,
                    {
                        label: registeredCompanyName,
                        amount: basket.totalPrice.toString(),
                    },
                    getLineItems(basket),
                );
            }
        }
    };

    const updateShippingMethod = async () => {
        const shippingMethod = shippingMethodsRef.current.find(({ selected }) => selected);

        if (!shippingMethod) {
            throw new Error('No shipping option found');
        }

        await API.updateShippingMethod(
            {
                shippingMethodId: parseFloat(shippingMethod.identifier),
                shipmentId: shippingMethod.shipmentId,
            },
            basket.id,
        );
    };

    const validateBasket = async () => {
        try {
            // Allocation is triggered immediately after Apple Pay session creation
            // to reserve stock early in the express flow.
            const allocationResult = await API.allocate(basket.id, PAYMENT_TYPES.APPLE);

            if (expressCheckoutOptions && shouldEnforceAllocationExpiry(allocationResult, expressCheckoutOptions)) {
                redirectOnOutOfStockTimer.enableUntil(allocationResult);
            }
        } catch (error) {
            if (error instanceof AuthorizationError) {
                throw error;
            }

            const message = getErrorMessageDetails(error);
            throw new StockAllocationError(message);
        }
    };

    const onPaymentAuthorized = async (event: ApplePayPaymentAuthorizedEvent, session: ApplePaySession) => {
        const { shippingContact, token } = event.payment;
        let { emailAddress } = shippingContact;

        try {
            const deliveryDetails = retrieveApplePayDeliveryDetails(shippingContact);
            const validateAddressResult = validateAddress(deliveryDetails);
            if (!validateAddressResult.isValid) {
                const message = validateAddressResult.errors.join(' ');
                throw new InvalidAddressDetailsError(message);
            }

            const forterToken = getForterToken();
            const payload = await applePayInstance.tokenize({ token });

            if (authToken) {
                const user = await API.getUser();

                if (user.email) {
                    emailAddress = user.email;
                }
            }

            await API.updateCustomerEmail(emailAddress, basket.number, basket.id);
            await updateShippingMethod();
            await API.updateDeliveryDetails(deliveryDetails, basket.id);

            const basketSummary = await HandleGetActiveBasketSummary(API, setExpressWidgetError);

            if (basketSummary) {
                const completeRequest = {
                    amount: basketSummary.totalPrice,
                    nonce: payload.nonce,
                    paymentMethod: PaymentMethodType.ApplePay,
                    forterToken,
                    basketId: basketSummary.id,
                    deviceData,
                    storePaymentMethodId: paymentMethodId,
                    basketSummary,
                    customerCommunicationPreferences: [],
                };
                const completeData = await API.completeBasket(completeRequest);

                session.completePayment(STATUS_SUCCESS);
                redirectOnOutOfStockTimer.disable();
                CheckoutRedirect.RedirectToOrderComplete(apiBasePath, completeData.orderRef, PAYMENT_TYPES.APPLE);
            }
        } catch (error) {
            redirectOnOutOfStockTimer.disable();

            handleComponentError(error, setExpressWidgetError, { shouldDispatchExpressErrorEvent: true });

            session.completePayment(STATUS_FAILURE);
        } finally {
            setIsApplePayFlowInProgress(false);
        }
    };

    const onApplePayButtonClick = async (eventClick: React.MouseEvent<HTMLButtonElement>) => {
        if (isApplePayFlowInProgress) {
            return;
        }

        setIsApplePayFlowInProgress(true);
        pushPaymentProviderSelectionEvent('ApplePayExpress', useMmSessionId);
        eventClick.preventDefault();

        try {
            const initialShippingMethods = shippingMethods.map((method, index) => ({
                ...method,
                selected: index === 0,
            }));
            const shippingMethod = getSelectedShippingMethod(initialShippingMethods);

            const paymentRequest = applePayInstance.createPaymentRequest({
                currencyCode: currency,
                total: {
                    label: registeredCompanyName,
                    amount: basket.totalPrice.toString(),
                },
                lineItems: getLineItems(basket, shippingMethod),
                shippingMethods: initialShippingMethods,
                requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
            });

            const ApplePaySessionCtor = getApplePaySession();
            if (!ApplePaySessionCtor) {
                setIsApplePayFlowInProgress(false);
                return;
            }

            const session = new ApplePaySessionCtor(APPLE_PAY_VERSION, paymentRequest);

            // I can to validate basket only after payment Session creation,
            // as AP required that session creation must be triggered only buy user gesture (async func reset user gesture event)
            await validateBasket();

            setShippingMethods(initialShippingMethods);

            session.onvalidatemerchant = (event: ApplePayValidateMerchantEvent) => {
                void onValidateMerchant(event, session);
            };
            session.onshippingcontactselected = (event: ApplePayShippingContactSelectedEvent) => {
                void onShippingContactSelected(event, session);
            };
            session.onshippingmethodselected = (event: ApplePayShippingMethodSelectedEvent) => {
                onShippingMethodSelected(event, session);
            };
            session.onpaymentauthorized = (event: ApplePayPaymentAuthorizedEvent) => {
                void onPaymentAuthorized(event, session);
            };
            session.oncancel = () => {
                redirectOnOutOfStockTimer.disable();
                setIsApplePayFlowInProgress(false);
            };

            session.begin();
        } catch (error) {
            redirectOnOutOfStockTimer.disable();
            setIsApplePayFlowInProgress(false);
            handleComponentError(error, setExpressWidgetError);
        }
    };

    useEffect(() => {
        void getNextShippingMethods(basket.id).then((nextShippingMethods) => {
            setShippingMethods(nextShippingMethods);
            dispatch({ type: 'SET_IS_APPLE_PAY_BUTTON_DISPLAYED', payload: true });
        });
    }, []);

    return shippingMethods.length ? (
        <button
            id="apple-pay-button"
            data-testid="apple-pay-button"
            className={`${styles.applePayButton} ${
                isApplePayFlowInProgress ? styles.applePayButtonDisabled : ''
            }`.trim()}
            onClick={onApplePayButtonClick}
            disabled={isApplePayFlowInProgress}
            aria-disabled={isApplePayFlowInProgress}
        />
    ) : null;
};

const ApplePayExpressButtonWrapper: React.FC = () => {
    const [applePayInstance, setApplePayInstance] = useState<ApplePay>();
    const { setExpressWidgetError } = useExpressWidgetError();
    const [paymentMethodId, setPaymentMethodId] = useState<number>();
    const [deviceData, setDeviceData] = useState<string>();
    const [state, dispatch] = useContext(WidgetContext);
    const { isApplePayButtonDisplayed, activeBasketSummary } = state;
    const API = useApi();

    useEffect(() => {
        const initApplePayButton = async () => {
            try {
                if (activeBasketSummary) {
                    const { clientToken } = await API.getBraintreeToken(activeBasketSummary.id);
                    const paymentMethods = await API.getActiveBasketPaymentMethods();

                    const applePayMethod = paymentMethods.find((p) => p.type === 'Spree::Gateway::BraintreeGateway');

                    const clientInstance = await client.create({ authorization: clientToken });
                    const nextApplePayInstance = await applePay.create({ client: clientInstance });
                    const deviceInstance = await dataCollector.create({
                        client: clientInstance,
                        paypal: true,
                    });

                    if (applePayMethod) {
                        setPaymentMethodId(applePayMethod.id);
                        setApplePayInstance(nextApplePayInstance);
                        setDeviceData(deviceInstance.deviceData ? deviceInstance.deviceData : 'noDeviceData');
                    } else {
                        throw new Error('could not find applePay method id!');
                    }
                }
            } catch (error) {
                handleComponentError(error, setExpressWidgetError);
                dispatch({ type: 'SET_IS_APPLE_PAY_BUTTON_DISPLAYED', payload: false });
            }
        };

        void initApplePayButton();
    }, [activeBasketSummary]);

    return applePayInstance && paymentMethodId && activeBasketSummary !== null && deviceData ? (
        <ApplePayExpressButton
            applePayInstance={applePayInstance}
            basket={activeBasketSummary}
            paymentMethodId={paymentMethodId}
            deviceData={deviceData}
        />
    ) : (
        <>
            {(isApplePayButtonDisplayed === null || activeBasketSummary === null) && (
                <div data-testid="appleSkeletonTestId">
                    <Skeleton color="#323232" height="55px" rounded="sm" width="100%" />
                </div>
            )}
        </>
    );
};

export default ApplePayExpressButtonWrapper;
